import type { MimirElectronContextBridgeObject } from '@mimir/common/desktop/ipc';
import type { LocalFfmpegInstallation } from '@mimir/common/types/ipcLocalFfmpeg';
import { LOCAL_FFMPEG_PORT_WINDOW_MESSAGE_TYPE } from '@mimir/common/types/ipcLocalFfmpeg';
import type { LocalFfmpegAccess, LocalFfmpegPort } from '@mimir/common/types/localFfmpeg';

interface PortWindowMessage {
  type: typeof LOCAL_FFMPEG_PORT_WINDOW_MESSAGE_TYPE;
  requestId: number;
  available: boolean;
}

function isPortWindowMessage(data: unknown): data is PortWindowMessage {
  return (
    typeof data === 'object'
    && data !== null
    && (data as { type?: unknown }).type === LOCAL_FFMPEG_PORT_WINDOW_MESSAGE_TYPE
  );
}

interface PendingPortRequest {
  resolve: (port: LocalFfmpegPort) => void;
  reject: (error: Error) => void;
}

export async function createLocalFfmpegAccess(
  mimirElectron: MimirElectronContextBridgeObject,
): Promise<LocalFfmpegAccess | null> {
  const { ipcLocalFfmpeg, postLocalFfmpegPort } = mimirElectron;
  if (ipcLocalFfmpeg === undefined || postLocalFfmpegPort === undefined) {
    return null;
  }

  let installation: LocalFfmpegInstallation | null;
  try {
    installation = await ipcLocalFfmpeg.getFfmpeg();
  } catch {
    return null;
  }
  if (installation === null) {
    return null;
  }

  const pending = new Map<number, PendingPortRequest>();
  let nextRequestId = 1;

  window.addEventListener('message', (event: MessageEvent) => {
    if (!isPortWindowMessage(event.data)) {
      return;
    }
    const entry = pending.get(event.data.requestId);
    if (entry === undefined) {
      return;
    }
    pending.delete(event.data.requestId);
    const port = event.ports[0];
    if (!event.data.available || port === undefined) {
      entry.reject(new Error('local-ffmpeg: main process reported ffmpeg unavailable'));
      return;
    }
    entry.resolve(port);
  });

  return {
    installation,
    requestPort(): Promise<LocalFfmpegPort> {
      const requestId = nextRequestId++;
      return new Promise<LocalFfmpegPort>((resolve, reject) => {
        pending.set(requestId, { resolve, reject });
        postLocalFfmpegPort(requestId);
      });
    },
  };
}
